import {
	CheckCircleOutlined,
	CloudSyncOutlined,
	CloudUploadOutlined,
	DeleteOutlined,
	EditOutlined,
	HomeOutlined,
	SearchOutlined,
	PlusOutlined,
	EyeOutlined
} from "@ant-design/icons";
import {
	Button,
	Card,
	Empty,
	Input,
	Popconfirm,
	Space,
	Table,
	Tag,
	Tooltip,
} from "antd";
import React, { useEffect, useState } from "react";
import { deleteClient, getAllClients } from "../../services/clientService";
import { syncClients } from "../../services/clientSyncService";
import { getSyncConfig } from "../../services/syncService";
import { emitSyncEvent } from "../../utils/syncUtils";
import { getClientFormPermissions } from "./configs/clientForm.config.permissions";
import { useConfig } from "../../contexts/ConfigContext";
import { findIdentityDocument } from "../../constants/identityDocuments";
import PageSpinner from "../Spinner/PageSpinner";

const ClientList = ({ refreshTrigger, onOpenForm, onViewAddresses, onAddClient }) => {
	const { country } = useConfig();
	const [clients, setClients] = useState([]);
	const [loading, setLoading] = useState(true);
	const [syncingAll, setSyncingAll] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const permissions = getClientFormPermissions(country?.code);
	const isMobile = window.innerWidth < 768;
	const [config, setConfig] = useState({
		interval: 30,
		intervalUnit: "segundos",
	});
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
	});

	// Cargar la configuración al inicio
	useEffect(() => {
		const fetchConfig = async () => {
			try {
				const syncConfig = await getSyncConfig();
				setConfig(syncConfig);
			} catch (error) {
				console.error("Error al cargar configuración:", error);
			}
		};

		fetchConfig();
	}, []);

	// Cargar clientes al inicio y cuando cambie el trigger
	useEffect(() => {
		loadClients();
	}, [refreshTrigger]);

	// Actualizar la lista según el intervalo configurado
	useEffect(() => {
		const interval = setInterval(() => {
			loadClients();
		}, config.interval * (config.intervalUnit === "segundos" ? 1000 : config.intervalUnit === "minutos" ? 60000 : 3600000)); // Convertir a milisegundos

		// Limpiar intervalo al desmontar
		return () => clearInterval(interval);
	}, [config]);

	const loadClients = async () => {
		try {
			console.log("Cargando clientes...");
			setLoading(true);
			const clientList = await getAllClients();
			setClients(clientList);
		} catch (error) {
			window.messageApi.error("Error al cargar clientes: " + error.message);
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (id) => {
		try {
			await deleteClient(id);
			// Actualizar la lista de clientes
			setClients(clients.filter((client) => client.id !== id));
			window.messageApi.success("Cliente eliminado correctamente");
		} catch (error) {
			window.messageApi.error("Error al eliminar cliente: " + error.message);
		}
	};

	const handleOpenForm = (client) => {
		if (onOpenForm) {
			onOpenForm(client);
		}
	};

	const handleViewAddresses = (client) => {
		if (onViewAddresses) {
			onViewAddresses(client);
		}
	};

	// Renderizar el estado de sincronización
	const renderSyncStatus = (pendingSync, lastSynced) => {
		if (pendingSync === 1) {
			return (
				<Tooltip title="Pendiente de sincronizar">
					<Tag icon={<CloudUploadOutlined />} color="warning">
						Pendiente
					</Tag>
				</Tooltip>
			);
		} else {
			const syncDate = lastSynced
				? new Date(lastSynced).toLocaleString()
				: "Nunca";

			return (
				<Tooltip title={`Última sincronización: ${syncDate}`}>
					<Tag icon={<CheckCircleOutlined />} color="success">
						Sincronizado
					</Tag>
				</Tooltip>
			);
		}
	};

	// Sincronizar todos los clientes
	const handleSyncAll = async () => {
		try {
			setSyncingAll(true);
			emitSyncEvent("Sincronizando clientes...", false);
			await syncClients();
			// Recargar la lista para reflejar los cambios
			await loadClients();
			emitSyncEvent("Sincronizando completada", true);
		} catch (error) {
			emitSyncEvent("Error al sincronizar clientes", true);
		} finally {
			setSyncingAll(false);
		}
	};

	// Filtrar clientes según el término de búsqueda
	const filteredClients = clients.filter((client) => {
		if (!searchTerm) return true;

		const searchValue = searchTerm.toLowerCase();

		// Buscar en todos los campos principales del cliente
		return (
			(client.id && client.id.toString().toLowerCase().includes(searchValue)) ||
			(client.No && client.No.toString().toLowerCase().includes(searchValue)) ||
			(client.name && client.name.toLowerCase().includes(searchValue)) ||
			(client.Tipo_Documento_SrI &&
				client.Tipo_Documento_SrI.toLowerCase().includes(searchValue)) ||
			(client.Numero_Identificacion_fiscal &&
				client.Numero_Identificacion_fiscal.toLowerCase().includes(
					searchValue
				)) ||
			(client.Customer_Price_Group &&
				client.Customer_Price_Group.toLowerCase().includes(searchValue)) ||
			(client.Telefono &&
				client.Telefono.toLowerCase().includes(searchValue)) ||
			(client.Mobil && client.Mobil.toLowerCase().includes(searchValue)) ||
			(client.Poblacion &&
				client.Poblacion.toLowerCase().includes(searchValue)) ||
			(client.POS_Discount &&
				client.POS_Discount.toString().toLowerCase().includes(searchValue))
		);
	});

	const columns = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
			hidden: true,
		},
		{
			title: "No.",
			dataIndex: "No",
			key: "No",
			render: (No) => No || "Pendiente",
		},
		{
			title: "Nombre",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "Tipo documento",
			dataIndex: "Tipo_Documento_SrI",
			key: "Tipo_Documento_SrI",
			responsive: ["lg"],
			render: (tipoDoc) =>
				findIdentityDocument(country?.code, tipoDoc)?.label || tipoDoc || "Cedula",
		},
		{
			title: "Número de cédula/RUC/Passaporte",
			dataIndex: "Numero_Identificacion_fiscal",
			key: "Numero_Identificacion_fiscal",
			responsive: ["md"],
		},
		{
			title: "Grupo de precios",
			dataIndex: "Customer_Price_Group",
			key: "Customer_Price_Group",
			responsive: ["md"],
		},
		{
			title: "Teléfono",
			dataIndex: "Telefono",
			key: "Telefono",
			responsive: ["lg"],
		},
		{
			title: "Móvil",
			dataIndex: "Mobil",
			key: "Mobil",
			responsive: ["md"],
		},
		{
			title: "Ciudad",
			dataIndex: "Poblacion",
			key: "Poblacion",
			responsive: ["lg"],
		},
		{
			title: "Descuento TPV (%)",
			dataIndex: "POS_Discount",
			key: "POS_Discount",
			render: (discount) => (discount || 0) + "%",
		},
		{
			title: "Estado",
			key: "syncStatus",
			render: (_, record) =>
				renderSyncStatus(record.pendingSync, record.lastSynced),
		},
		{
			title: "Acciones",
			key: "actions",
			render: (_, record) => (
				<Space size="middle">
					{
						(permissions.canUpdate || permissions.canRead) &&
						<Button
							type="primary"
							icon={permissions.canUpdate ? <EditOutlined /> : <EyeOutlined />}
							size="small"
							onClick={() => handleOpenForm(record)}
						>
							{permissions.canUpdate ? 'Editar' : 'Ver'}
						</Button>}
					{
						(permissions.canRead) &&
						<Button
							icon={<HomeOutlined />}
							size="small"
							onClick={() => handleViewAddresses(record)}
							title="Ver direcciones"
						>
							Direcciones
						</Button>
					}
					{permissions.canDelete &&
						<Popconfirm
							title="¿Está seguro de que desea eliminar este cliente?"
							onConfirm={() => handleDelete(record.id)}
							okText="Sí"
							cancelText="No"
						>
							<Button
								disabled danger icon={<DeleteOutlined />} size="small">
								Eliminar
							</Button>
						</Popconfirm>
					}
				</Space>
			),
		},
	];

	if (loading) {
		return <PageSpinner />;
	}

	if (clients.length === 0) {
		return <Empty description="No hay clientes registrados aún" />;
	}

	return (
		<div className="client-list-container">
			<Card
				title={isMobile ? "Clientes" : "Clientes registrados"}
				extra={
					<Space>
						{
							permissions.canCreate &&
							<Button
								icon={<PlusOutlined />}
								type="default"
								onClick={onAddClient}
							>
								{isMobile ? "" : "Agregar cliente"}
							</Button>
						}
						<Button
							icon={<CloudSyncOutlined />}
							onClick={handleSyncAll}
							loading={syncingAll}
							type="primary"
						>
							{isMobile ? "" : "Sincronizar clientes"}
						</Button>
					</Space>
				}
			>
				<div style={{ marginBottom: 16 }}>
					<Input
						placeholder="Buscar clientes por cualquier campo..."
						prefix={<SearchOutlined />}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						allowClear
						size="large"
					/>
				</div>

				{filteredClients.length === 0 && searchTerm ? (
					<Empty
						description={`No se encontraron clientes que coincidan con "${searchTerm}"`}
					/>
				) : (
					<div className="client-table-wrapper">
						<Table
							columns={columns}
							dataSource={filteredClients.map((client) => ({
								...client,
								key: client.id,
							}))}
							pagination={{
								...pagination,
								showSizeChanger: true,
								showQuickJumper: true,
								pageSizeOptions: ["5", "10", "20", "50"],
								showTotal: (total, range) =>
									`${range[0]}-${range[1]} de ${total} elementos`,
							}}
							onChange={(newPagination) => setPagination(newPagination)}
							bordered
							size="middle"
							scroll={{
								x: "max-content",
								y: "100vh",
							}}
							loading={syncingAll}
						/>
					</div>
				)}
			</Card>
		</div>
	);
};

export default ClientList;
